import { PHASE } from "../../common/constants.ts";
import type { TradeEventTeams } from "../../common/types.ts";
import { player, trade } from "../core/index.ts";
import { getTeammateJerseyNumbers } from "../core/player/genJerseyNumber.ts";
import { ValueChangeCalculator } from "../core/team/ValueChangeCalculator.ts";
import { idb } from "../db/index.ts";
import { g, helpers } from "../util/index.ts";
import { getClient, getLeagueIdForLid, getLobbyState } from "./index.ts";
import {
	syncNow,
	type MpTradeAsset,
	type MpTradeEventPayload,
} from "./sync.ts";

// ============================================================================
// Ownership ledger seeding
// ============================================================================

export const collectAssets = async () => {
	const players = await idb.cache.players.indexGetAll("playersByTid", [
		0,
		Number.POSITIVE_INFINITY,
	]);
	const picks = await idb.cache.draftPicks.getAll();
	return [
		...players.map((p) => ({ kind: "player", id: p.pid, tid: p.tid })),
		...picks.map((dp) => ({ kind: "pick", id: dp.dpid, tid: dp.tid })),
	];
};

export const seedAssets = async (leagueId: string) => {
	const assets = await collectAssets();
	const { data, error } = await getClient().rpc("seed_assets", {
		p_league_id: leagueId,
		p_assets: assets,
	});
	if (error) {
		throw new Error(`Asset seeding failed: ${error.message}`);
	}
	return data as { status: string; count: number };
};

// Backfill for leagues started before the ledger existed. Only the
// commissioner can seed, and only when the ledger is empty; everyone else's
// call is a cheap no-op error we swallow.
export const ensureAssetsSeeded = async (leagueId: string) => {
	try {
		const { count, error } = await getClient()
			.from("league_assets")
			.select("*", { count: "exact", head: true })
			.eq("league_id", leagueId);
		if (error || (count ?? 0) > 0) {
			return;
		}
		await seedAssets(leagueId);
	} catch {
		// Non-commissioners land here; the commissioner's client will seed
	}
};

// ============================================================================
// Building trade events (proposer side - this is where randomness gets rolled)
// ============================================================================

export const buildTradeEvent = async (
	teams: [
		{ tid: number; pids: number[]; dpids: number[] },
		{ tid: number; pids: number[]; dpids: number[] },
	],
): Promise<{ assets: MpTradeAsset[]; event: MpTradeEventPayload }> => {
	const duringSeason = g.get("phase") <= PHASE.PLAYOFFS;
	const assets: MpTradeAsset[] = [];
	const eventTeams: TradeEventTeams = [{ assets: [] }, { assets: [] }];

	// Jersey numbers a previous iteration of this loop already assigned, so two
	// incoming players don't pre-roll the same number
	const assignedNumbers = new Map<number, string[]>();

	for (const j of [0, 1] as const) {
		const k = j === 0 ? 1 : 0;

		for (const pid of teams[j].pids) {
			const p = await idb.cache.players.get(pid);
			if (!p) {
				throw new Error(`Invalid pid ${pid}`);
			}

			let jerseyNumber: string | undefined;
			if (duringSeason) {
				// Mirror of processTrade's conflict logic, but without mutating:
				// numbers on the receiving team (except players leaving in this
				// same trade), numbers already pre-rolled for this team, and the
				// current numbers of the other players being traded
				const toTid = teams[k].tid;
				const taken = await getTeammateJerseyNumbers(toTid, [
					p.pid,
					...teams[k].pids,
				]);
				taken.push(...(assignedNumbers.get(toTid) ?? []));
				for (const j2 of [0, 1] as const) {
					for (const otherPid of teams[j2].pids) {
						if (otherPid !== pid) {
							const p2 = await idb.cache.players.get(otherPid);
							const num = p2 ? helpers.getJerseyNumber(p2) : undefined;
							if (num !== undefined) {
								taken.push(num);
							}
						}
					}
				}

				jerseyNumber = await player.genJerseyNumber(p, taken);
				if (jerseyNumber !== undefined) {
					const list = assignedNumbers.get(toTid) ?? [];
					list.push(jerseyNumber);
					assignedNumbers.set(toTid, list);
				}
			}

			assets.push({
				kind: "player",
				id: pid,
				from_tid: teams[j].tid,
				to_tid: teams[k].tid,
				jerseyNumber,
			});

			eventTeams[k]!.assets.push({
				pid: p.pid,
				name: `${p.firstName} ${p.lastName}`,
				contract: p.contract,
				ratingsIndex: p.ratings.length - 1,
				statsIndex: p.stats.length - (duringSeason ? 0 : 1),
			});
		}

		for (const dpid of teams[j].dpids) {
			const dp = await idb.cache.draftPicks.get(dpid);
			if (!dp) {
				throw new Error(`Invalid dpid ${dpid}`);
			}

			assets.push({
				kind: "pick",
				id: dpid,
				from_tid: teams[j].tid,
				to_tid: teams[k].tid,
			});

			eventTeams[k]!.assets.push({
				dpid: dp.dpid,
				season: dp.season,
				round: dp.round,
				originalTid: dp.originalTid,
			});
		}
	}

	return {
		assets,
		event: {
			tids: [teams[0].tid, teams[1].tid],
			assets,
			eventTeams,
			phase: g.get("phase"),
			season: g.get("season"),
		},
	};
};

// ============================================================================
// Committing
// ============================================================================

type CommitResult =
	| { status: "committed"; seq: number }
	| { status: "needs_offer" }
	| {
			status: "budget_exceeded";
			tid: number;
			used: number;
			budget: number;
			checkpoint: string;
	  }
	| {
			status: "conflict";
			reason: string;
			kind?: "player" | "pick";
			id?: number;
			current_tid?: number;
	  };

export const budgetExceededMessage = (
	result: Extract<CommitResult, { status: "budget_exceeded" }>,
	myTid: number,
) => {
	const who = result.tid === myTid ? "You have" : "The other team has";
	return `${who} used all ${result.budget} trades allowed during the ${result.checkpoint} checkpoint. Budgets reset when everyone readies up and the league advances.`;
};

const conflictMessage = async (
	result: CommitResult & { status: "conflict" },
) => {
	if (result.kind === "player" && result.id !== undefined) {
		const p = await idb.cache.players.get(result.id);
		const name = p ? `${p.firstName} ${p.lastName}` : "A player in this deal";
		return `Trade failed: ${name} is no longer available (someone else just completed a trade). The rosters may have changed - rebuild the trade.`;
	}
	return "Trade failed: an asset in this deal just changed hands. Rebuild the trade and try again.";
};

export const commitTrade = async (
	leagueId: string,
	assets: MpTradeAsset[],
	event: MpTradeEventPayload,
): Promise<CommitResult> => {
	const { data, error } = await getClient().rpc("commit_trade", {
		p_league_id: leagueId,
		p_assets: assets,
		p_event: event,
	});
	if (error) {
		throw new Error(error.message);
	}
	return data as CommitResult;
};

// Multiplayer replacement for trade.propose (the AI-team path). Returns
// undefined when this league isn't multiplayer, so the caller can fall through
// to the original code.
export const mpProposeTrade = async (): Promise<
	[boolean, string | null] | undefined
> => {
	const lid = g.get("lid");
	if (typeof lid !== "number") {
		return undefined;
	}
	const leagueId = await getLeagueIdForLid(lid);
	if (!leagueId) {
		return undefined;
	}

	// Same phase restriction as trade.propose
	if (
		g.get("phase") >= PHASE.AFTER_TRADE_DEADLINE &&
		g.get("phase") <= PHASE.PLAYOFFS
	) {
		return [
			false,
			`Error! You're not allowed to make trades ${
				g.get("phase") === PHASE.AFTER_TRADE_DEADLINE
					? "after the trade deadline"
					: "now"
			}.`,
		];
	}

	const { teams } = await trade.get();

	// Human counterpart -> must go through an offer so they consent
	const lobby = await getLobbyState(leagueId);
	const humanTids = lobby.members
		.map((m) => m.tid)
		.filter((tid): tid is number => tid !== null);
	if (humanTids.includes(teams[1].tid)) {
		return [
			false,
			"That team is controlled by a human. Use the Send Trade Offer button to propose this trade to them.",
		];
	}

	// Same validation + AI evaluation as trade.propose
	const s = await trade.summary(teams);
	if (s.warning) {
		return [false, null];
	}

	const dv = await new ValueChangeCalculator().evaluate({
		tid: teams[1].tid,
		pidsAdd: teams[0].pids,
		pidsRemove: teams[1].pids,
		dpidsAdd: teams[0].dpids,
		dpidsRemove: teams[1].dpids,
		tradingPartnerTid: g.get("userTid"),
	});

	if (dv <= 0) {
		let message;
		if (dv > -2) {
			message = "Close, but not quite good enough.";
		} else if (dv > -5) {
			message = "That's not a good deal for me.";
		} else {
			message = "What, are you crazy?!";
		}
		return [false, `Trade rejected! "${message}"`];
	}

	const { assets, event } = await buildTradeEvent(teams as any);
	const result = await commitTrade(leagueId, assets, event);

	if (result.status === "conflict") {
		return [false, await conflictMessage(result)];
	}
	if (result.status === "budget_exceeded") {
		return [false, budgetExceededMessage(result, teams[0].tid)];
	}
	if (result.status === "needs_offer") {
		return [
			false,
			"That team is controlled by a human. Use the Send Trade Offer button instead.",
		];
	}

	// The roster changes land by applying our own event from the log - the
	// same code path every other client runs
	await trade.clear();
	await syncNow(leagueId);

	return [true, 'Trade accepted! "Nice doing business with you!"'];
};

// ============================================================================
// v16.2: undoing accidental trades (commissioner)
// ============================================================================

export type RecentTrade = {
	seq: number;
	tids: [number, number];
	// teamNames[i] and received[i] correspond to tids[i]
	teamNames: [string, string];
	received: TradeEventTeams;
	createdAt: string;
};

// Trades made in the current checkpoint window that can still be undone (not
// yet undone, not baked into a published snapshot). Commissioner UI only.
export const listRecentTrades = async (
	leagueId: string,
	checkpointSeq: number,
): Promise<RecentTrade[]> => {
	const { data, error } = await getClient()
		.from("league_events")
		.select("seq, type, payload, created_at")
		.eq("league_id", leagueId)
		.in("type", ["trade", "trade_undo"])
		.gt("seq", checkpointSeq)
		.order("seq", { ascending: false });
	if (error) {
		throw new Error(error.message);
	}
	const rows = (data ?? []) as {
		seq: number;
		type: string;
		payload: any;
		created_at: string;
	}[];

	const undoneSeqs = new Set(
		rows
			.filter((r) => r.type === "trade_undo")
			.map((r) => Number(r.payload?.undoes_seq)),
	);
	const teamInfo = g.get("teamInfoCache");
	const teamName = (tid: number) => {
		const t = teamInfo[tid];
		return t ? `${t.region} ${t.name}` : `Team ${tid}`;
	};

	return rows
		.filter((r) => r.type === "trade" && !undoneSeqs.has(r.seq))
		.map((r) => {
			const tids = r.payload.tids as [number, number];
			return {
				seq: r.seq,
				tids,
				teamNames: [teamName(tids[0]), teamName(tids[1])] as [string, string],
				received: r.payload.eventTeams as TradeEventTeams,
				createdAt: r.created_at,
			};
		});
};

const undoErrorMessage = (result: {
	status: string;
	reason?: string;
	current_tid?: number;
}) => {
	switch (result.status) {
		case "not_commissioner":
			return "Only the commissioner can undo a trade.";
		case "sim_in_progress":
			return "You can't undo a trade while the league is simulating.";
		case "trade_not_found":
			return "That trade could not be found.";
		case "already_published":
			return "This trade is from a past checkpoint and is already locked in.";
		case "already_undone":
			return "This trade has already been undone.";
		case "conflict":
			return "This trade can't be undone - one of its players or picks has since changed hands. Undo the later trade first.";
		default:
			return `Undo failed: ${result.status}`;
	}
};

// Commissioner: undo a trade from the current checkpoint window. Returns a
// human-readable error string on failure, or undefined on success.
export const mpUndoTrade = async (
	tradeSeq: number,
): Promise<string | undefined> => {
	const lid = g.get("lid");
	if (typeof lid !== "number") {
		return "Not in a multiplayer league.";
	}
	const leagueId = await getLeagueIdForLid(lid);
	if (!leagueId) {
		return "Not in a multiplayer league.";
	}
	const { data, error } = await getClient().rpc("undo_trade", {
		p_league_id: leagueId,
		p_trade_seq: tradeSeq,
	});
	if (error) {
		throw new Error(error.message);
	}
	const result = data as {
		status: string;
		reason?: string;
		current_tid?: number;
	};
	if (result.status !== "undone") {
		return undoErrorMessage(result);
	}
	await syncNow(leagueId);
	return undefined;
};
